<template>
  <section class="mb-8 flex items-center justify-between">
    <div class="flex items-center gap-x-8">
      <h1>{{ $t("intents") }}</h1>
      <BaseSearch @on-searching="setFilterText" />
    </div>
    <BaseButton @click="handleNewIntent">{{ $t("addNew") }}</BaseButton>
  </section>
  <BaseDataList
    class="space-y-6"
    :tot-data="filteredData.length"
    :is-searching="!!filterText"
  >
    <IntentCard
      v-for="intent in filteredData"
      v-bind="intent"
      :key="intent.id"
    />
  </BaseDataList>
  
  <!-- Pagination Controls -->
  <div v-if="!filterText && totalPages > 1" class="flex justify-center items-center gap-4 mt-8">
    <button 
      @click="prevPage" 
      :disabled="!hasPrevPage"
      class="px-4 py-2 rounded bg-gray-600 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-500"
    >
      {{ $t('previous') || 'Previous' }}
    </button>
    
    <div class="flex gap-2">
      <button
        v-for="page in totalPages"
        :key="page - 1"
        @click="goToPage(page - 1)"
        :class="[
          'px-3 py-2 rounded transition-all duration-200',
          currentPage === page - 1 
            ? 'border-b-2 border-blue-500 font-semibold'
            : 'pb-2'
        ]"
      >
        {{ page }}
      </button>
    </div>
    
    <button 
      @click="nextPage" 
      :disabled="!hasNextPage"
      class="px-4 py-2 rounded bg-gray-600 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-500"
    >
      {{ $t('next') || 'Next' }}
    </button>
  </div>
</template>

<script setup>
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { ref, computed } from "vue";
import useModalStore from "@/stores/modal";
import useIntentStore from "@/stores/intent";
import IntentCard from "@/components/intents/IntentCard.vue";
import IntentForm from "@/components/intents/IntentForm.vue";
import useFilterData from "@/composables/useFilterData";

const modal = useModalStore();
const { t } = useI18n();
const currentPage = ref(0);

/**
 * Handle new itent.
 */
function handleNewIntent() {
  modal.openModal({
    title: t("newIntent"),
    component: IntentForm,
  });
}

/**
 * Get intents data with pagination.
 */
const { paginatedIntentList, intentList } = storeToRefs(useIntentStore());

// Flatten the current page or use all intents when searching
const currentPageData = computed(() => {
  if (filterText.value) {
    // When searching, use all intents flattened
    return intentList.value;
  }
  // When not searching, use the current page
  return paginatedIntentList.value[currentPage.value] || [];
});

const { filterText, setFilterText, filteredData } = useFilterData(currentPageData, [
  "name",
]);

// Pagination controls
const totalPages = computed(() => paginatedIntentList.value.length);
const hasPrevPage = computed(() => currentPage.value > 0);
const hasNextPage = computed(() => currentPage.value < totalPages.value - 1);

function goToPage(page) {
  if (page >= 0 && page < totalPages.value) {
    currentPage.value = page;
  }
}

function prevPage() {
  if (hasPrevPage.value) {
    currentPage.value--;
  }
}

function nextPage() {
  if (hasNextPage.value) {
    currentPage.value++;
  }
}
</script>
